import React, { FC, useEffect, useRef, useState } from "react"
import axios from 'axios'

import { debounce, isClientSide } from "@utils"
import { CardFragmentFragment } from "@graphqlTypes"

import { Search } from "@helpscout/hs-marketing-shared-components"

import Cards, { CARDS_VARIANTS } from "@components/Cards/Cards"

declare global {
  interface Window {
    ga: any
  }
}

type SearchInterfaceProps = {
  onClickClose?: () => void,
}

const SearchInterface: FC<SearchInterfaceProps> = ({ onClickClose }) => {
  const [results, setResults] = useState<CardFragmentFragment[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [hasError, setHasError] = useState<boolean>(false)
  const [query, setQuery] = useState<string>("")
  const debouncedHandleSearchRef = useRef<(_query?: string) => void | undefined>()

  const handleClickCard = () => {
    onClickClose && onClickClose()
  }

  const handleSearch = (_query: string) => {
    setQuery(_query)
  }

  const performSearch = (_query: string) => axios
    .get(`/api/search?query=${_query}`)
    .then(response => response.data)
    .then(data => {
      setResults(data.results)
      setIsLoading(false)
      setHasError(false)
    })
    .catch(() => {
      setResults([])
      setIsLoading(false)
      setHasError(true)
    })

  useEffect(() => {
    if (!isClientSide()) return
    debouncedHandleSearchRef.current = debounce(performSearch, 300)
  }, [])

  useEffect(() => {
    if (!isClientSide() || !debouncedHandleSearchRef.current) return
    debouncedHandleSearchRef.current(query)
  }, [query])

  return (
    <Search
      hasError={hasError}
      hasResults={results.length > 0}
      isLoading={isLoading}
      isSearching={isLoading}
      onSearch={handleSearch}
      onClickClose={onClickClose}
      query={query}
    >
      <>
        {results.length > 0 && (
          <Cards
            items={results}
            onClickCard={handleClickCard}
            variant={CARDS_VARIANTS.COMPACT_WITH_DESCRIPTION}
          />
        )}
      </>
    </Search>
  )
}

export default SearchInterface
